"use client"

import { useState } from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useForm } from "react-hook-form"

const PhoneIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
)

const ArrowLeftIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </svg>
)

type ForgotPasswordFormValues = {
  identifier: string
}

export default function ForgotPasswordPage() {
  const t = useTranslations("pages.auth.forgot")
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>()

  async function onSubmit(data: ForgotPasswordFormValues) {
    setLoading(true)
    setErrorMessage("")
    setSuccessMessage("")

    // The server route always returns a generic success regardless of whether
    // the account exists, so we surface the same message here. That removes
    // the enumeration signal that the previous client-side flow leaked.
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: data.identifier }),
      })

      const result = (await res.json().catch(() => null)) as { message?: string } | null

      setSuccessMessage(result?.message || t("genericSuccess"))
    } catch (error) {
      console.error("Password reset error:", error)
      // Even on network error, show the same generic message — never leak
      // information about which identifiers were tried.
      setSuccessMessage(t("genericSuccess"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-md space-y-6 sm:space-y-8">
        <div className="space-y-2 text-center">
          <div className="flex items-center justify-center gap-2 mb-4 sm:mb-6">
            <img src="/images/booth-logo-black.png" alt="BOOTH" className="h-6" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-black">{t("title")}</h1>
          <p className="text-sm text-zinc-600">
            {t("subtitle")}
          </p>
        </div>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">{errorMessage}</div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm p-3 rounded-lg">
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="identifier" className="text-sm font-medium text-black">
              {t("identifierLabel")}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">
                <PhoneIcon />
              </span>
              <Input
                id="identifier"
                type="text"
                placeholder={t("identifierPlaceholder")}
                className="bg-white border-zinc-300 text-black h-11 pl-10"
                {...register("identifier", {
                  required: t("identifierRequired"),
                })}
              />
            </div>
            {errors.identifier && <p className="text-sm text-red-600">{errors.identifier.message}</p>}
          </div>

          <Button
            type="submit"
            className="w-full bg-black hover:bg-zinc-900 text-white font-semibold h-11 rounded-full"
            disabled={loading}
          >
            {loading ? t("submitting") : t("submit")}
          </Button>
        </form>

        <Link
          href="/auth/login"
          className="flex items-center justify-center gap-2 text-sm text-zinc-600 hover:text-black transition-colors"
        >
          <ArrowLeftIcon />
          {t("back")}
        </Link>
      </div>
    </div>
  )
}
